import React, { useEffect, useState } from "react";
import { Container, Button, Box, Alert, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  TABLE_HEAD_FUND_REBALANCE_LOCATES,
  findFundAbbById,
  hashAction,
  hashPosition,
} from "../../../../component/Methods";
import Locates from "./components/Locates";
import LocatesNotFound from "../../Popup/LocatesNotFound";
import { useDispatch, useSelector } from "react-redux";
import { selectActiveRequests } from "../../../../Redux/Reducers/selectors";
import { stageTwoOfRebalance } from "../../../../Redux/Reducers/rebalancerSlice";
import Step2Skeleton from "../../Skeletons/Step2Skeleton";
import { useRebalancerState } from "../../../../hooks/useOptimizedState";

const FundStep4 = ({ setCurrentStep, onPrevious }) => {
  const dispatch = useDispatch();
  const activeRequests = useSelector(selectActiveRequests);
  const { AllTrades, custodians, funds } = useRebalancerState();

  const [popupOpen, setPopupOpen] = useState(false);
  const [locatesData, setLocatesData] = useState({});

  const isLoadingLocates = activeRequests.includes("rebalancer_locates");

  const groupByCustodian = (orders, funds) => {
    const updatedOrders = orders?.map((order) => {
      const matchedFund = funds?.find(
        (fund) => fund?.abbreviation === order?.fund
      );
      if (matchedFund && matchedFund?.accounts) {
        const account = matchedFund?.accounts[order?.custodian];
        if (account) {
          return { ...order, custodianId: account?.custodian };
        }
      }
      return { ...order, custodianId: "N/A" };
    });

    return updatedOrders?.reduce((grouped, trade) => {
      if (!grouped[trade?.custodianId]) {
        grouped[trade?.custodianId] = [];
      }
      grouped[trade?.custodianId].push(trade);
      return grouped;
    }, {});
  };

  useEffect(() => {
    if (AllTrades && Array.isArray(AllTrades?.realOrders)) {
      const TickerTrades = AllTrades?.realOrders;
      if (TickerTrades?.length > 0) {
        const filteredTrades = TickerTrades.filter((trade) => {
          const tradeType =
            hashAction(trade.action) + hashPosition(trade.position);
          return tradeType.toLowerCase() == "sellshort";
        });

        if (filteredTrades?.length > 0) {
          const updatedData = filteredTrades.map((trade, index) => {
            const residual =
              trade?.residual || trade?.residual === 0
                ? trade?.residual
                : trade?.shareCount;
            return {
              ticker: trade?.ticker?.stock
                ? `${trade?.ticker?.symbol?.toUpperCase()}/${trade?.ticker?.stock?.toUpperCase()}`
                : trade?.ticker?.symbol?.toUpperCase(),
              shareCount: trade.shareCount,
              residual: residual,
              available:
                trade?.availableLocates || trade?.availableLocates === 0
                  ? trade.availableLocates
                  : residual,
              custodian: trade.custodian,
              fund: findFundAbbById(trade?.fund, funds),
              id: trade?.tradeId,
              key: index,
            };
          });

          const groupedData = groupByCustodian(updatedData, funds);
          setLocatesData(groupedData);
        }
      }
    }
  }, [AllTrades]);

  const groupedOrders = locatesData;

  const handleLocatesDataChange = (updatedData, custodian) => {
    setLocatesData((prevData) => {
      const updatedGroupedOrders = { ...prevData };
      updatedGroupedOrders[custodian] = updatedData;
      return updatedGroupedOrders;
    });
  };

  const updateAvailableLocates = (realOrders, locatesData) => {
    const allLocates = Object.values(locatesData).flat();

    return realOrders.map((trade) => {
      const locatesMatch = allLocates?.find(
        (locate) =>
          locate.ticker.toLowerCase() === trade.ticker.symbol.toLowerCase() &&
          locate.id === trade.tradeId &&
          locate.shareCount === trade.shareCount
      );

      if (locatesMatch) {
        return {
          ...trade,
          availableLocates: parseFloat(locatesMatch.available),
        };
      }

      return trade;
    });
  };

  const onNext = () => {
    if (locatesData) {
      const updatedTickerTrades = updateAvailableLocates(
        AllTrades.realOrders,
        locatesData
      );

      const allTrades = {
        ...AllTrades,
        realOrders: updatedTickerTrades,
      };
      dispatch(stageTwoOfRebalance(allTrades));
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  return (
    <Container
      disableGutters
      maxWidth="100%"
      sx={{
        marginTop: 2,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        width: "100%",
      }}
    >
      {isLoadingLocates ? (
        <Step2Skeleton />
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              flexShrink: 0,
            }}
          >
            <Alert severity="info">
              Locates Available column is editable, enter the numbers or use ⋮
              icon to auto fill from API.
            </Alert>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={onNext}
                sx={{ width: "150px", height: "40px" }}
              >
                Next
              </Button>
            </Box>
          </Box>

          {Object.keys(groupedOrders).length === 0 ? (
            <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
              No data available
            </Typography>
          ) : (
            <Box
              sx={{
                flex: "1 1 0px",
                minHeight: 0,
                minWidth: 0,
                maxWidth: "100%",
                display: "flex",
                flexWrap: "nowrap",
                overflowX: "auto",
                overflowY: "hidden",
                gap: 2,
                px: 2,
                pb: 1,
                pr: 3,
                boxSizing: "border-box",
                alignItems: "stretch",
              }}
            >
              {Object.keys(groupedOrders).map((custodian) => (
                <Box
                  key={custodian}
                  sx={{
                    width: "400px",
                    flexShrink: 0,
                    height: "100%",
                    maxHeight: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Locates
                    tickerTrades={groupedOrders[custodian]}
                    setLocatesData={handleLocatesDataChange}
                    tableHeader={TABLE_HEAD_FUND_REBALANCE_LOCATES}
                    custodian={custodian}
                    custodians={custodians}
                  />
                </Box>
              ))}
              <Box sx={{ flexShrink: 0, width: 24 }} />
            </Box>
          )}
        </Box>
      )}

      <LocatesNotFound open={popupOpen} onClose={handleClosePopup} />
    </Container>
  );
};

export default FundStep4;
